import React, { useState, useEffect, useCallback, useRef } from "react";
import { MapPin, Loader2, Maximize2, Minimize2 } from "lucide-react";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// Load Google Maps script once globally
let googleMapsLoading = false;
let googleMapsLoaded = false;
const loadCallbacks = [];

function loadGoogleMaps(callback) {
  if (window.google?.maps?.DirectionsService) {
    callback();
    return;
  }

  loadCallbacks.push(callback);

  if (googleMapsLoading) return;
  googleMapsLoading = true;

  // Remove any existing Google Maps scripts without API key
  const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
  existingScripts.forEach((s) => s.remove());

  // Clean up google maps objects if loaded without key
  if (window.google?.maps) {
    delete window.google.maps;
  }

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    googleMapsLoaded = true;
    googleMapsLoading = false;
    loadCallbacks.forEach((cb) => cb());
    loadCallbacks.length = 0;
  };
  script.onerror = () => {
    googleMapsLoading = false;
  };
  document.head.appendChild(script);
}

const RouteMapPreview = ({ pickup, dropoff, stops = [] }) => {
  const [mapsReady, setMapsReady] = useState(false);
  const [directions, setDirections] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const requestIdRef = useRef(0);

  const hasRoute = !!(pickup && dropoff);
  const filledStops = stops.filter((s) => s && s.trim() !== "");

  // Load Google Maps
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    loadGoogleMaps(() => setMapsReady(true));
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: { lat: -27.47, lng: 153.02 },
      zoom: 10,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: "cooperative",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "simplified" }] },
      ],
    });

    const renderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: false,
      polylineOptions: {
        strokeColor: "#C8A95B",
        strokeWeight: 4,
        strokeOpacity: 0.9,
      },
    });
    renderer.setMap(map);

    mapInstanceRef.current = map;
    directionsRendererRef.current = renderer;
  }, [mapsReady]);

  // Fetch directions when route changes
  const fetchDirections = useCallback(() => {
    if (!mapsReady || !hasRoute || !window.google?.maps) return;

    const currentId = ++requestIdRef.current;

    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = filledStops.map((stop) => ({
      location: stop,
      stopover: true,
    }));

    directionsService.route(
      {
        origin: pickup,
        destination: dropoff,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (currentId !== requestIdRef.current) return;
        if (status === "OK") {
          setDirections(result);
          setError("");
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
          }
        } else {
          setError("Route not found");
          setDirections(null);
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setDirections({ routes: [] });
          }
        }
      }
    );
  }, [mapsReady, pickup, dropoff, filledStops.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasRoute) {
      const timer = setTimeout(fetchDirections, 600);
      return () => clearTimeout(timer);
    } else {
      setDirections(null);
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections({ routes: [] });
      }
    }
  }, [fetchDirections, hasRoute]);

  // Don't render anything if no route
  if (!hasRoute || !GOOGLE_MAPS_KEY) return null;

  if (!mapsReady) {
    return (
      <div className="flex items-center justify-center h-40 bg-muted/30 rounded-xl border border-black/[0.06]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-xl overflow-hidden border border-black/[0.06] transition-all duration-300 ${
        expanded ? "h-[400px]" : "h-[200px]"
      }`}
      data-testid="route-map-preview"
    >
      {/* Expand/Collapse toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="absolute top-2 right-2 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-md hover:bg-white transition-colors"
        data-testid="map-expand-btn"
      >
        {expanded ? (
          <Minimize2 className="h-4 w-4 text-gray-600" />
        ) : (
          <Maximize2 className="h-4 w-4 text-gray-600" />
        )}
      </button>

      {/* Route info badge */}
      {directions && (
        <div className="absolute bottom-2 left-2 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-[#C8A95B]" />
          <span className="text-xs font-medium text-gray-700">
            {filledStops.length > 0
              ? `${filledStops.length} stop${filledStops.length > 1 ? "s" : ""} along route`
              : "Direct route"}
          </span>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      )}

      <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

export default RouteMapPreview;
